/*
 * Environment form: pure model, no React and nothing that touches `window` (so it imports `environmentLabels`, not
 * `environmentHelpers`, which needs the auth client). The branch-pattern rules are the API's own: they come from
 * @hangar/shared (`branchPatternProblem`, `BRANCH_PATTERNS_MAX`, `environmentGuardrailIssues`), the same functions the
 * API validates with. Also here: the form values, validation, the create and patch payloads and the mapping of the
 * API's failures.
 */
import { BRANCH_PATTERNS_MAX, DEFAULT_ENVIRONMENT_COLORS, ENVIRONMENT_COLOR_PATTERN, branchPatternProblem, environmentGuardrailIssues, type EnvKind, type Environment, type Project } from '@hangar/shared';
import { SLUG_HINT, SLUG_RE, slugify } from '@/lib/format';
import { blockerLabel, liveEnvironments, type Blocker } from './environmentLabels';

export type Field = 'name' | 'slug' | 'sortOrder' | 'allowedBranchPatterns' | 'untrustedRefsBlocked' | 'color';
export type FieldErrors = Partial<Record<Field, string>>;

/** Fields in the order the form lists them: the first invalid one gets the focus on submit. */
export const FIELD_ORDER: Field[] = ['name', 'slug', 'sortOrder', 'allowedBranchPatterns', 'untrustedRefsBlocked', 'color'];

export const NAME_MAX = 120;
export const SORT_ORDER_MAX = 100_000;

/** Everything the user can type or pick. `order` stays text (digits only) until submit. */
export interface EnvFormValues {
  name: string;
  slug: string;
  /** Once the slug was typed (or the environment exists) the name no longer rewrites it. */
  slugTouched: boolean;
  kind: EnvKind;
  /** `null` = the kind's default color. */
  color: string | null;
  isProtected: boolean;
  patterns: string[];
  /** The pattern typed but not added yet; it counts on save. */
  draft: string;
  /** Forks and untrusted refs blocked. */
  blocked: boolean;
  order: string;
}

/** "After the last live environment": the highest sort order plus 10 (the defaults use 10, 20, 30). */
export const nextSortOrder = (project: Project): number => Math.max(0, ...liveEnvironments(project).map((e) => e.sortOrder)) + 10;

/** The guardrail fields of the start: unprotected, any branch, untrusted refs blocked (a new environment) or the stored ones. */
function initialGuardrail(env?: Environment): Pick<EnvFormValues, 'isProtected' | 'patterns' | 'blocked'> {
  return { isProtected: env?.isProtected ?? false, patterns: env?.allowedBranchPatterns ?? [], blocked: env?.untrustedRefsBlocked ?? true };
}

/** The identity fields of the start: empty with the staging kind (a new environment) or the stored ones. */
function initialIdentity(env?: Environment): Pick<EnvFormValues, 'name' | 'slug' | 'slugTouched' | 'kind' | 'color'> {
  return { name: env?.name ?? '', slug: env?.slug ?? '', slugTouched: Boolean(env), kind: env?.kind ?? 'staging', color: env?.color ?? null };
}

/** A new environment starts as an unprotected staging one that blocks untrusted refs; an edit starts from the environment. */
export function initialEnvValues(project: Project, env?: Environment): EnvFormValues {
  return { ...initialIdentity(env), ...initialGuardrail(env), draft: '', order: String(env?.sortOrder ?? nextSortOrder(project)) };
}

/* ---------- branch patterns (the API's rules) ---------- */

/**
 * Why `pattern` cannot join `list`: already there, over the API's limit of `BRANCH_PATTERNS_MAX` patterns, or not an
 * allowed-branch glob (`branchPatternProblem`). `undefined` when it can.
 */
export function patternRejection(list: readonly string[], pattern: string): string | undefined {
  if (list.includes(pattern)) return 'already listed';
  if (list.length >= BRANCH_PATTERNS_MAX) return `at most ${BRANCH_PATTERNS_MAX} patterns`;
  return branchPatternProblem(pattern);
}

export type PatternAdd = { kind: 'empty' } | { kind: 'rejected'; message: string } | { kind: 'added'; list: string[] };

/** Adds the typed draft to the chips: nothing when empty, a "pattern: reason" message when the API would refuse it. */
export function addPattern(list: string[], draft: string): PatternAdd {
  const pattern = draft.trim();
  if (!pattern) return { kind: 'empty' };
  const why = patternRejection(list, pattern);
  return why ? { kind: 'rejected', message: `${pattern}: ${why}` } : { kind: 'added', list: [...list, pattern] };
}

/** The chips plus the typed-but-not-added pattern (not yet validated: the API checks it on save). */
export function effectivePatterns(patterns: string[], draft: string): string[] {
  const pending = draft.trim();
  return pending && !patterns.includes(pending) ? [...patterns, pending] : patterns;
}

/* ---------- derived view and validation ---------- */

export interface EnvView {
  /** The color shown: the picked one, else the kind's default. */
  shownColor: string;
  allPatterns: string[];
  orderNumber: number;
  local: FieldErrors;
}

export function deriveEnvView(v: EnvFormValues): EnvView {
  const allPatterns = effectivePatterns(v.patterns, v.draft);
  return { shownColor: v.color ?? DEFAULT_ENVIRONMENT_COLORS[v.kind], allPatterns, orderNumber: Number(v.order), local: validateEnv(v, allPatterns) };
}

export function validateName(name: string): string | undefined {
  const trimmed = name.trim();
  if (!trimmed) return 'Name is required';
  return trimmed.length > NAME_MAX ? `Keep the name under ${NAME_MAX} characters` : undefined;
}

export const validateSlug = (slug: string): string | undefined => (SLUG_RE.test(slug) ? undefined : SLUG_HINT);

export function validateOrder(order: string): string | undefined {
  const valid = /^\d+$/.test(order.trim()) && Number(order) <= SORT_ORDER_MAX;
  return valid ? undefined : `A whole number from 0 to ${SORT_ORDER_MAX} (lower builds first)`;
}

/** Guardrail messages of @hangar/shared (a protected environment: a branch, none matching every branch, forks blocked). */
export function validateGuardrail(v: Pick<EnvFormValues, 'isProtected' | 'blocked'>, allPatterns: string[]): Pick<FieldErrors, 'allowedBranchPatterns' | 'untrustedRefsBlocked'> {
  const issues = environmentGuardrailIssues({ isProtected: v.isProtected, allowedBranchPatterns: allPatterns, untrustedRefsBlocked: v.blocked });
  return {
    allowedBranchPatterns: issues.find((i) => i.path[0] === 'allowedBranchPatterns')?.message,
    untrustedRefsBlocked: issues.find((i) => i.path[0] === 'untrustedRefsBlocked')?.message,
  };
}

/** Local verdicts, shown only after the first submit. */
export function validateEnv(v: EnvFormValues, allPatterns: string[]): FieldErrors {
  return { name: validateName(v.name), slug: validateSlug(v.slug), sortOrder: validateOrder(v.order), ...validateGuardrail(v, allPatterns) };
}

/** The first field with a message, in form order: where the focus goes on a failed submit. */
export const firstInvalidField = (errors: FieldErrors): Field | undefined => FIELD_ORDER.find((f) => errors[f]);

/** The API's message first, then the local verdict once the form was submitted. */
export const shownError = (server: FieldErrors, local: FieldErrors, submitted: boolean, field: Field): string | undefined => server[field] || (submitted ? local[field] : undefined);

/* ---------- edits that touch more than one value ---------- */

/** Typing the name also suggests the slug until the slug was typed by hand. */
export function withName(v: EnvFormValues, name: string): EnvFormValues {
  return { ...v, name, ...(v.slugTouched ? {} : { slug: slugify(name) }) };
}

/** A typed slug is lowercase and stops following the name. */
export const withSlug = (v: EnvFormValues, slug: string): EnvFormValues => ({ ...v, slugTouched: true, slug: slug.toLowerCase() });

/** A protected environment always blocks untrusted refs: the select follows and locks. */
export const withProtected = (v: EnvFormValues, on: boolean): EnvFormValues => ({ ...v, isProtected: on, blocked: on ? true : v.blocked });

/** Execution order accepts digits only. */
export const digitsOnly = (text: string): string => text.replace(/[^\d]/g, '');

/* ---------- payloads ---------- */

/** A hex color the API accepts, or none (the kind's default then applies). */
export const validColor = (color: string | null): color is string => color !== null && ENVIRONMENT_COLOR_PATTERN.test(color);

/** `POST /projects/{id}/environments` body. */
export function buildCreateBody(v: EnvFormValues, view: Pick<EnvView, 'allPatterns' | 'orderNumber'>) {
  return {
    slug: v.slug,
    name: v.name.trim(),
    kind: v.kind,
    sortOrder: view.orderNumber,
    ...(validColor(v.color) ? { color: v.color } : {}),
    isProtected: v.isProtected,
    allowedBranchPatterns: view.allPatterns,
    untrustedRefsBlocked: v.blocked,
  };
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

/** A color changes when it differs from the stored one and is clearable (`null`) or a valid hex. */
const colorPatch = (color: string | null, stored: string | null | undefined): { color: string | null } | Record<string, never> => {
  const changed = (color ?? null) !== (stored ?? null);
  return changed && (color === null || validColor(color)) ? { color } : {};
};

/** `PATCH /environments/{id}` body with only what changed (`kind` never changes); `{}` when nothing did. */
export function buildPatchBody(env: Environment, v: EnvFormValues, view: Pick<EnvView, 'allPatterns' | 'orderNumber'>) {
  return {
    ...(v.name.trim() !== env.name ? { name: v.name.trim() } : {}),
    ...(v.slug !== env.slug ? { slug: v.slug } : {}),
    ...(view.orderNumber !== env.sortOrder ? { sortOrder: view.orderNumber } : {}),
    ...colorPatch(v.color, env.color),
    ...(v.isProtected !== env.isProtected ? { isProtected: v.isProtected } : {}),
    ...(!sameList(view.allPatterns, env.allowedBranchPatterns) ? { allowedBranchPatterns: view.allPatterns } : {}),
    ...(v.blocked !== env.untrustedRefsBlocked ? { untrustedRefsBlocked: v.blocked } : {}),
  };
}

/* ---------- messages ---------- */

const patternsText = (saved: Pick<Environment, 'isProtected' | 'allowedBranchPatterns'>): string => {
  const list = saved.allowedBranchPatterns.join(', ');
  if (saved.isProtected) return `Protected · ${list}`;
  return list || 'Any branch';
};

export const addedToast = (saved: Environment): { title: string; description: string } => ({ title: `${saved.slug} added`, description: patternsText(saved) });
export const savedToast = (saved: Environment): { title: string; description: string } => ({ title: `${saved.slug} saved`, description: 'Applies to the next build that targets it.' });

export const formTitle = (env?: Environment): string => (env ? `Edit environment: ${env.slug}` : 'New environment');

/* ---------- the API's failures ---------- */

/** What the form needs to know of a failed request (see `environmentFailure.ts` for how it is read off `ApiError`). */
export interface FailureInfo {
  status: number;
  code: string;
  message: string;
  toastTitle: string;
  /** Per-field messages of a 400 / 422. */
  fieldErrors: FieldErrors;
  /** What still uses the environment, on a 409 that refuses a slug change. */
  blockers: Blocker[];
}

/** The problem alert to show: the failure itself, a plain message, or the 412 "reload" message that keeps the code. */
export type ProblemSpec = { kind: 'same' } | { kind: 'message'; message: string } | { kind: 'stale'; message: string };

export interface FailureOutcome {
  /** Replaces the per-field messages. */
  server?: FieldErrors;
  problem?: ProblemSpec;
  toast?: { title: string; description?: string };
}

export const STALE_PROJECT_MESSAGE = 'This project changed since the page loaded (another environment or a limit was saved). The page reloaded it: review and save again.';
const same: ProblemSpec = { kind: 'same' };

/** A 409: the slug is in use (builds, shadow runs or requirements name it), already taken, or something else. */
function conflictOutcome(f: FailureInfo, slugChanged: boolean): FailureOutcome {
  // The slug of an environment builds, shadow runs or requirements already name can no longer change.
  if (f.blockers.length && slugChanged) return { server: { slug: `The slug can no longer change: ${f.blockers.map(blockerLabel).join(', ')} use it.` } };
  return /slug|already has/i.test(f.message) ? { server: { slug: f.message } } : { problem: same };
}

/** Maps a failed create / save to what the form shows. `info` is `null` when the failure is not an API problem. */
export function failureOutcome(info: FailureInfo | null, slugChanged: boolean): FailureOutcome {
  if (!info) return { toast: { title: 'Could not save the environment' } };
  switch (info.status) {
    case 501:
      return { problem: { kind: 'message', message: `Not available yet on this Hangar (${info.code}): ${info.message}` } };
    case 412:
      return { problem: { kind: 'stale', message: STALE_PROJECT_MESSAGE } };
    case 409:
      return conflictOutcome(info, slugChanged);
    case 422:
    case 400:
      return Object.keys(info.fieldErrors).length ? { server: info.fieldErrors } : { problem: same };
    case 403:
      return { problem: same };
    default:
      return { toast: { title: info.toastTitle, description: `${info.code} · ${info.message}` } };
  }
}
